"use client";
import { useRef, useContext, useState, useEffect, useId, useMemo } from 'react';
import { Settings, Palette } from 'lucide-react';
import { createPortal } from "react-dom";


const openDialog = (id) => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('lunar:open-dialog', { detail: { id } }));
  }
};
const componentLibrary = {};
const BuilderControlsPopover = () => null;

const useActiveOverlayPosition = (overlayRect) => {
  return useMemo(() => {
    if (!overlayRect) return {};
    const cx = overlayRect.left + (overlayRect.width / 2);
    const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1000;
    let leftPos = cx < 88 ? 8 : (cx > windowWidth - 88 ? 'auto' : cx);
    let transform = cx < 88 || cx > windowWidth - 88 ? 'none' : 'translateX(-50%)';
    return { position: 'fixed', top: Math.max(overlayRect.top - 24, 42), left: leftPos, right: cx > windowWidth - 88 ? 8 : 'auto', zIndex: 10002, transform };
  }, [overlayRect]);
};
const isVideo = (url) => url && typeof url === 'string' && (url.match(/\.(mp4|webm|ogg)$/i));
export default function ScrollGroup({
sectionId,
components = [],
image,
mobileImage,
imageMobileRatio,
scrollEffect = "none", // 'none' | 'parallax' | 'sticky' | 'stacked'
scrim = "none", // 'none' | 'dark' | 'brand'
templateGap = 0, // Gap between templates in px
enableBlur = false, // Toggle for blur effect
transparentNextSection = false, // Toggle for keeping immediate next section transparent
disableEffects = false, // New prop to disable internal effects (for Staging/Export)
onUpdate,
updateComponent, // passed from Canvas to render children
componentLibrary // Passed from parent (Builder) to break circular dependency
}) {
const { activeElementId = '__EXPORTED__', setActiveElementId = () => {}, activePopoverId = null, setActivePopoverId = () => {}, localData, isStaging = true } = {};
  // Context values mocked for export library
const { canvasWidth = '100%' } = {};
  // Context values mocked for export library
const fallbackId = useId();
const isMobileSimulation = !disableEffects && (() => {
if (!canvasWidth || canvasWidth === '100%') return false;
const width = parseInt(canvasWidth, 10);
return !isNaN(width) && width <= 768;
})();
const elementId = sectionId || `scroll-group-${fallbackId.replace(/:/g, '')}`;
const [isClientActive, setIsClientActive] = useState(false);
useEffect(() => {
setIsClientActive(activeElementId === elementId);
}, [activeElementId, elementId]);
const isActive = isClientActive;
const myPopoverBase = `popover-${elementId}`;
const showSettings = activePopoverId && activePopoverId.startsWith(myPopoverBase);
const isStyleOpen = activePopoverId === `${myPopoverBase}-style`;
const sectionRef = useRef(null);
const [popoverPosition, setPopoverPosition] = useState(null);
const [overlayRect, setOverlayRect] = useState(null);
const currentImage = (typeof window !== 'undefined' && isMobileSimulation && mobileImage) ? mobileImage : image;
const hasVideo = isVideo(currentImage);
const effectiveScrollEffect = hasVideo ? 'none' : scrollEffect;
const isSticky = effectiveScrollEffect === 'sticky';
const isStacked = effectiveScrollEffect === 'stacked';
const isParallax = effectiveScrollEffect === 'parallax';
const isNone = effectiveScrollEffect === 'none';
const baseStyle = {
backgroundImage: hasVideo ? 'none' : (currentImage ? `url("${currentImage}")` : 'none'),
backgroundPosition: isNone ? "top center" : "center",
backgroundRepeat: "no-repeat",
backgroundSize: "cover",
width: "100%",
position: "relative",
display: "flex",
flexDirection: "column",
...(isParallax && !hasVideo ? { backgroundAttachment: "fixed" } : {})
};
const stickyContainerStyle = {
position: "relative",
width: '100%',
minHeight: "100vh",
isolation: 'isolate',
display: 'grid',
gridTemplateAreas: '"stack"'
};
const stickyBackgroundStyle = {
gridArea: 'stack',
position: "sticky",
top: 0,
height: "100vh",
width: "100%",
backgroundImage: hasVideo ? 'none' : (currentImage ? `url("${currentImage}")` : 'none'),
backgroundPosition: "center",
backgroundRepeat: "no-repeat",
backgroundSize: "cover",
zIndex: -1, // Behind content
alignSelf: 'start'
};
const stackedStyle = {
position: "relative",
width: "100%",
minHeight: "100vh", // Build height to ensure effect is visible
backgroundImage: hasVideo ? 'none' : (currentImage ? `url("${currentImage}")` : 'none'),
backgroundPosition: "center",
backgroundRepeat: "no-repeat",
backgroundSize: "cover"
};
const contentStyle = {
gridArea: 'stack',
position: "relative",
zIndex: 1
};
useEffect(() => {
if (isActive && sectionRef.current) {
const updatePosition = () => {
if (sectionRef.current) {
const rect = sectionRef.current.getBoundingClientRect();
setOverlayRect(rect);
if (showSettings) {
setPopoverPosition({
top: rect.top,
left: rect.left + rect.width / 2
});
}
}
};
updatePosition();
window.addEventListener('scroll', updatePosition, true);
window.addEventListener('resize', updatePosition);
return () => {
window.removeEventListener('scroll', updatePosition, true);
window.removeEventListener('resize', updatePosition);
};
}
}, [isActive, showSettings]);
const blurContainerRef = useRef(null);
const whiteOverlayRef = useRef(null);
useEffect(() => {
if (disableEffects) return;
if (!isStacked || !sectionRef.current) {
if (whiteOverlayRef.current) whiteOverlayRef.current.style.opacity = 0;
if (blurContainerRef.current) blurContainerRef.current.style.filter = 'none';
if (sectionRef.current) {
const wrapper = sectionRef.current.parentElement;
let sibling = wrapper ? wrapper.nextElementSibling : sectionRef.current.nextElementSibling;
let count = 0;
while (sibling && count < 20) {
if (sibling.style) {
sibling.style.filter = '';
sibling.style.backgroundColor = '';
}
sibling = sibling.nextElementSibling;
count++;
}
}
return;
}
const handleScroll = () => {
if (!sectionRef.current) return;
const wrapper = sectionRef.current.parentElement;
const nextElement = wrapper ? wrapper.nextElementSibling : sectionRef.current.nextElementSibling;
if (!nextElement) return;
const rect = nextElement.getBoundingClientRect();
const winH = window.innerHeight;
const startPoint = winH;
const endPoint = winH * 0.5;
const current = rect.top;
let progress = 0;
if (current < startPoint) {
progress = (startPoint - current) / (startPoint - endPoint);
}
progress = Math.max(0, Math.min(1, progress));
if (whiteOverlayRef.current) {
whiteOverlayRef.current.style.opacity = enableBlur ? progress : 0;
}
if (blurContainerRef.current) {
blurContainerRef.current.style.filter = enableBlur ? `blur(${progress * 10}px)` : 'none';
}
if (nextElement && nextElement.style) {
if (enableBlur) {
const inverseBlur = (1 - progress) * 10;
nextElement.style.filter = `blur(${inverseBlur}px)`;
const maskGradient = `linear-gradient(to bottom, transparent 0px, black 80px, black 100%)`;
nextElement.style.maskImage = maskGradient;
nextElement.style.webkitMaskImage = maskGradient;
} else {
nextElement.style.filter = '';
nextElement.style.maskImage = '';
nextElement.style.webkitMaskImage = '';
}
}
let sibling = nextElement;
const targetBg = 'var(--background-neutral--default, #ffffff)';
let count = 0;
const MAX_SIBLINGS = 20;
if (transparentNextSection) {
if (nextElement && nextElement.style) {
nextElement.style.setProperty('background-color', 'transparent', 'important');
}
sibling = nextElement.nextElementSibling;
}
while (sibling && count < MAX_SIBLINGS) {
if (sibling.style) {
sibling.style.setProperty('background-color', targetBg, 'important');
}
sibling = sibling.nextElementSibling;
count++;
}
};
const scrollTarget = document.getElementById('canvas-scroll-container') || window;
scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
handleScroll(); // Initial check
return () => {
scrollTarget.removeEventListener('scroll', handleScroll);
if (sectionRef.current) {
const wrapper = sectionRef.current.parentElement;
let sibling = wrapper ? wrapper.nextElementSibling : sectionRef.current.nextElementSibling;
let count = 0;
while (sibling && count < 20) {
if (sibling.style) {
sibling.style.filter = '';
sibling.style.maskImage = '';
sibling.style.webkitMaskImage = '';
sibling.style.backgroundColor = '';
}
sibling = sibling.nextElementSibling;
count++;
}
}
};
}, [isStacked, enableBlur, transparentNextSection, disableEffects]);
const handleStyleSettingsClick = (e) => {
e.preventDefault();
e.stopPropagation();
if (!isStyleOpen && sectionRef.current) {
const rect = sectionRef.current.getBoundingClientRect();
setPopoverPosition({ top: rect.top, left: rect.left + rect.width / 2 });
}
setActivePopoverId(prev => prev === `${myPopoverBase}-style` ? null : `${myPopoverBase}-style`);
};
const handleClick = (e) => {
if (isStaging) return;
e.stopPropagation();
if (setActiveElementId) setActiveElementId(elementId);
};
const overlayStyle = useActiveOverlayPosition(overlayRect);

const renderScrim = () => {
if (scrim === 'none') return null;
return (
<div
style={{
position: 'absolute',
inset: 0,
zIndex: 0,
opacity: 0.5,
backgroundColor: scrim === 'brand'
? 'var(--content-brand--brand-strong)'
: 'var(--background-neutral--neutral-strong)'
}}
/>
);
};
const renderVideo = (isParallaxVideo = false) => {
if (!hasVideo) return null;
if (isParallaxVideo) {
return (
<div style={{ position: "absolute", inset: 0, overflow: "hidden", zIndex: 0 }}>
<video src={currentImage} autoPlay loop muted playsInline style={{ position: "sticky", top: 0, width: "100%", height: "100vh", objectFit: "cover" }} />
</div>
);
}
return (
<video src={currentImage} autoPlay loop muted playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }} />
);
};
const renderChildren = () => (
<div className="rv-parallaxContent" style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', zIndex: 1 }}>
{components.map((item, index) => {
const registryComponent = typeof COMPONENT_REGISTRY !== 'undefined' ? COMPONENT_REGISTRY[item.id] : null;
const Component = item.component || (componentLibrary ? Object.values(componentLibrary).flat().find(c => c.id === item.id)?.component : null) || registryComponent;
if (!Component) return null;
const effectiveId = item.uniqueId || item.id; // Fallback to id if uniqueId missing
const lookupId = item.sectionId || item.uniqueId;
const stagingOverride = localData ? (localData[lookupId] || localData[effectiveId]) : {};
const baseProps = item.props ? item.props : item;
const cleanBaseProps = item.props ? baseProps : (() => {
const { component, uniqueId, sectionId, id, ...rest } = baseProps;
return rest;
})();
const mergedProps = { ...cleanBaseProps, ...stagingOverride };
const gapStyle = (index > 0 && templateGap) ? { marginTop: `${templateGap}px` } : {};
return (
<div
key={`${item.uniqueId || item.id || 'comp'}-${index}`}
id={item.sectionId || item.uniqueId}
data-unique-id={item.uniqueId}
className="rv-componentWrapper"
style={gapStyle}
>
<Component
{...mergedProps}
sectionId={item.sectionId}
uniqueId={item.uniqueId}
updateComponent={updateComponent}
componentLibrary={componentLibrary}
/>
</div>
)
})}
</div>
);
const renderPopover = () => (
isActive && (
<BuilderControlsPopover
isOpen={showSettings}
onClose={() => setActivePopoverId(null)}
position={popoverPosition}
mode="style"
showScrollEffect={!isStaging && !hasVideo}
scrollEffect={scrollEffect}
onScrollEffectChange={(val) => onUpdate({ scrollEffect: val })}
showTemplateGapToggle={!isStaging}
templateGap={templateGap}
onTemplateGapChange={(val) => onUpdate({ templateGap: val })}
showImageSrc={!isStaging}
imageSrc={image}
onImageSrcChange={(val) => onUpdate({ image: val })}
showMobileRatio={false}
mobileRatio={imageMobileRatio}
showMobileImageSrc={!isStaging}
mobileImageSrc={mobileImage}
onMobileImageSrcChange={(val) => onUpdate({ mobileImage: val })}
showLinkType={false}
showVariant={false}
showUrl={false}
showBlurToggle={scrollEffect === 'stacked' && !isStaging}
enableBlur={enableBlur}
onEnableBlurChange={(val) => onUpdate({ enableBlur: val })}
showTransparentNextSectionToggle={scrollEffect === 'stacked' && !isStaging}
transparentNextSection={transparentNextSection}
onTransparentNextSectionChange={(val) => onUpdate({ transparentNextSection: val })}
showScrim={!isStaging}
scrim={scrim}
onScrimChange={(val) => onUpdate({ scrim: val })}
showFullWidthToggle={false}
popoverTitle="Scroll Settings"
/>
)
);
const wrapperStart = isMobileSimulation ? '' : '@media (max-width: 768px) {';
const wrapperEnd = isMobileSimulation ? '' : '}';
const mobileImageCss = mobileImage && !isVideo(mobileImage) ? `
${wrapperStart}
div[id="${elementId}"] {
background-image: url("${mobileImage}") !important;
}
${wrapperEnd}
` : '';
const stickyMobileImageCss = mobileImage && !isVideo(mobileImage) ? `
${wrapperStart}
div[id="${elementId}-bg"] {
background-image: url("${mobileImage}") !important;
}
${wrapperEnd}
` : '';
const mobileStyleTag = mobileImage ? (
<style dangerouslySetInnerHTML={{ __html: isSticky ? stickyMobileImageCss : mobileImageCss }} />
) : null;
if (isSticky) {
return (
<div
ref={sectionRef}
id={elementId}
onClick={handleClick}
className={`rv-parallaxGroup${isActive ? " rv-activeWrapper" : ""}`}
style={stickyContainerStyle}
suppressHydrationWarning={true}
>
<div id={`${elementId}-bg`} style={stickyBackgroundStyle}>
{renderVideo(false)}
{renderScrim()}
</div>
<div style={contentStyle}>
{renderChildren()}
</div>

{mobileStyleTag}
</div>
);
}
if (isStacked) {
return (
<div
ref={sectionRef}
id={elementId}
onClick={handleClick}
className={`rv-parallaxGroup${isActive ? " rv-activeWrapper" : ""}`}
style={stackedStyle}
suppressHydrationWarning={true}
>
{renderVideo(false)}
{renderScrim()}
{}
<div
ref={whiteOverlayRef}
className="rv-blendOverlay"
style={{ opacity: 0 }}
/>
{}
<div
ref={blurContainerRef}
style={{
position: 'relative',
zIndex: 1,
width: '100%',
height: '100%',
}}
>
{renderChildren()}
</div>

{mobileStyleTag}
</div>
);
}
return (
<div
ref={sectionRef}
id={elementId}
onClick={handleClick}
className={`rv-parallaxGroup${isActive ? " rv-activeWrapper" : ""}`}
style={baseStyle}
suppressHydrationWarning={true}
>
{renderVideo(isParallax)}
{renderScrim()}

{renderChildren()}

{mobileStyleTag}
</div>
);
}